import { Button, Card, CardContent, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField, Typography, Box, RadioGroup, FormControlLabel, Radio, IconButton, InputAdornment } from '@mui/material';
import * as React from 'react';
import { genreMoodLanguage } from '../../components/reusableComponents/genreMoodLanguage';
import { useState, useEffect } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLocation } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import dayjs from 'dayjs';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const minDate = new Date();

export default function ArtistCampaign({
    currencyList,
    stremingPlatform,
    setSongDuration,
    songDuration,
    setClips,
    clips,
    selectedDemo,
    songId,
    handleFormChange,
    formValues,
    budgetError,
    setBudgetError,
    releaseDateChangeHandler,
    formDateValues,
    setFormValues,
    setHasInvalidReferenceLink,
    setHasInvalidClipTime
}) {
    const { getMoodList, getLanguageList, getGenreList } = genreMoodLanguage();
    const location = useLocation().state;
    const [genreList, setGenreList] = useState([]);
    const [moodList, setMoodList] = useState([]);
    const [languageList, setLanguageList] = useState([]);
    const [platformList, setPlatformList] = useState([]);
    const [clipErrors, setClipErrors] = useState([]);
    const [mentionAccounts, setMentionAccounts] = useState(false);
    const [accountsList, setAccountsList] = useState(['']);
    const [dateTouched, setDateTouched] = useState({ startDate: false, endDate: false });

    const isDemo = !!selectedDemo;
    const displayName = isDemo ? selectedDemo?.audio_name : songId?.songTitle;

    const isValidUrl = (url = "") => {
        if (!url) return true;
        const pattern = /^(https?:\/\/)([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;
        return pattern.test(url.trim());
    };

    const validateAllReferenceLinks = (updatedClips) => {
        const hasInvalid = updatedClips.some(
            clip => clip.reference_link && !isValidUrl(clip.reference_link)
        );
        setHasInvalidReferenceLink(hasInvalid);
    };

    const isStartBeforeEnd = (start, end) => {
        if (!start || !end) return true;
        const [sm, ss] = start.split(':').map(Number);
        const [em, es] = end.split(':').map(Number);
        return sm * 60 + ss < em * 60 + es;
    };

    const validateClipTimes = (updatedClips) => {
        const hasInvalid = updatedClips.some(clip => {
            if (!clip.start_time || !clip.end_time) return false;
            return !isStartBeforeEnd(clip.start_time, clip.end_time);
        });
        setHasInvalidClipTime(hasInvalid);
    };

    const formatAccountName = (value) => {
        return value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9_]/g, '');
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const fetchedMoods = await getMoodList();
                setMoodList(fetchedMoods);

                const fetchedLanguages = await getLanguageList();
                setLanguageList(fetchedLanguages);

                const fetchedGenres = await getGenreList();
                setGenreList(fetchedGenres);

                const reelPlatform = await ApiService.get('reel_platforms');
                setPlatformList(reelPlatform?.data || []);
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        fetchData();
        return () => fetchData();
    }, []);

    useEffect(() => {
        if (selectedDemo?.release_date) {
            releaseDateChangeHandler(dayjs(selectedDemo.release_date), "releaseDate");
        }
    }, [selectedDemo]);

    const formatCurrency = (value) => {
        if (!value) return '';
        return new Intl.NumberFormat('en-IN').format(value);
    };

    const handleNonNegativeCountChange = (fieldName, rawValue) => {
        if (rawValue === '') {
            setFormValues((prev) => ({ ...prev, [fieldName]: '' }));
            return;
        }

        const parsedValue = Number(rawValue);
        if (Number.isNaN(parsedValue)) return;

        const clampedValue = Math.max(0, Math.floor(parsedValue));
        setFormValues((prev) => ({ ...prev, [fieldName]: String(clampedValue) }));
    };

    const startDateValue = formDateValues?.startDate ? dayjs(formDateValues.startDate) : null;
    const endDateValue = formDateValues?.endDate ? dayjs(formDateValues.endDate) : null;
    const isStartDateValid = !startDateValue || startDateValue.isValid();
    const isEndDateValid = !endDateValue || endDateValue.isValid();
    const hasInvalidDateRange = Boolean(
        startDateValue &&
        endDateValue &&
        isStartDateValid &&
        isEndDateValid &&
        endDateValue.isBefore(startDateValue, 'day')
    );

    const startDateErrorMessage = (() => {
        if (!dateTouched.startDate) return '';
        if (!startDateValue) return 'Promotion start date is required.';
        if (!isStartDateValid) return 'Enter a valid start date.';
        return '';
    })();

    const endDateErrorMessage = (() => {
        if (hasInvalidDateRange) return 'Promotion end date must be on or after start date.';
        if (!dateTouched.endDate) return '';
        if (!endDateValue) return 'Promotion end date is required.';
        if (!isEndDateValid) return 'Enter a valid end date.';
        return '';
    })();

    return (
        <Box>
            {/* Top Section: Two Cards Side by Side */}
            <Grid container spacing={3} mb={3}>
                {/* 1. Song Details */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1 }}>
                            Song Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size='small'
                                    fullWidth
                                    label="Song Name"
                                    value={displayName || ''}
                                    disabled
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Select Mood *</InputLabel>
                                    <Select
                                        value={isDemo ? selectedDemo?.mood?.id : formValues?.selectedMood}
                                        name='selectedMood'
                                        onChange={(e) => handleFormChange(e)}
                                        input={<OutlinedInput label="Select Mood *" />}
                                        disabled={songId?.mood?.id || selectedDemo?.mood?.id}
                                    >
                                        {moodList?.map((mood) => (
                                            <MenuItem key={mood.id} value={mood.id}>
                                                <ListItemText primary={mood.mood} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Select Genre *</InputLabel>
                                    <Select
                                        name='selectedGenres'
                                        value={isDemo ? selectedDemo?.genre?.id : formValues?.selectedGenres}
                                        onChange={(e) => handleFormChange(e)}
                                        input={<OutlinedInput label="Select Genre *" />}
                                        disabled={songId?.genre?.id || selectedDemo?.genre?.id}
                                    >
                                        {genreList?.map((genre) => (
                                            <MenuItem key={genre.id} value={genre.id}>
                                                <ListItemText primary={genre.genre} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Select Language *</InputLabel>
                                    <Select
                                        value={isDemo ? selectedDemo?.language?.id : formValues?.selectedLanguage || ''}
                                        name='selectedLanguage'
                                        onChange={(e) => handleFormChange(e)}
                                        input={<OutlinedInput label="Select Language *" />}
                                        disabled={songId?.language?.id || selectedDemo?.language?.id}
                                    >
                                        {languageList?.map((language) => (
                                            <MenuItem key={language.id} value={language.id}>
                                                <ListItemText primary={language.language} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {selectedDemo && (<Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Release Date"
                                        value={formDateValues?.releaseDate ? dayjs(formDateValues.releaseDate) : null}
                                        onChange={(date) => releaseDateChangeHandler(date, "releaseDate")}
                                        disabled={Boolean(selectedDemo?.release_date)}
                                        renderInput={(params) => (
                                            <TextField {...params} size="small" fullWidth />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>)}
                        </Grid>
                    </Box>
                    <Box sx={{ mb: 3, mt: 3, backgroundColor: '#fff', borderRadius: 2, p: 2 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1 }} >
                            Deliverables
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth size='small'>
                                    <InputLabel>Select Platform *</InputLabel>
                                    <Select
                                        multiple
                                        value={formValues?.selectedCampaignPlatform || []}
                                        name="selectedCampaignPlatform"
                                        onChange={(e) => handleFormChange(e)}
                                        input={<OutlinedInput label="Select Platform *" />}
                                        renderValue={(selected) =>
                                            selected.map((value) =>
                                                platformList.find(platform => platform.platformId === value)?.platformName
                                            ).join(', ')
                                        }
                                    >
                                        {platformList?.map((platform) => (
                                            <MenuItem key={platform.platformId} value={platform.platformId}>
                                                <ListItemText primary={platform?.platformName} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Reel Count"
                                    type="number"
                                    name="targetReelsCount"
                                    value={formValues?.targetReelsCount || ""}
                                    onChange={(e) => handleNonNegativeCountChange('targetReelsCount', e.target.value)}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder="Number of reels"
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Story Count"
                                    type="number"
                                    name="targetStoryCount"
                                    value={formValues?.targetStoryCount || ""}
                                    onChange={(e) => handleNonNegativeCountChange('targetStoryCount', e.target.value)}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder="Number of stories"
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Shorts Count"
                                    type="number"
                                    name="targetShortsCount"
                                    value={formValues?.targetShortsCount || ""}
                                    onChange={(e) => handleNonNegativeCountChange('targetShortsCount', e.target.value)}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder="Number of shorts"
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Image/GIF Post Count"
                                    type="number"
                                    name="targetPostsCount"
                                    value={formValues?.targetPostsCount || ""}
                                    onChange={(e) => handleNonNegativeCountChange('targetPostsCount', e.target.value)}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder="Number of posts"
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    multiline
                                    rows={3}
                                    size='small'
                                    fullWidth
                                    label="Hashtags to Mention"
                                    name='suggHashtag'
                                    value={formValues?.suggHashtag}
                                    onChange={(e) => handleFormChange(e)}
                                    placeholder="e.g., #Music #NewRelease #Trending"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" mb={1}>
                                    Account to Mention
                                </Typography>
                                <RadioGroup
                                    row
                                    value={mentionAccounts}
                                    onChange={(e) => setMentionAccounts(e.target.value === 'true')}
                                >
                                    <FormControlLabel value={true} control={<Radio />} label="Yes" />
                                    <FormControlLabel value={false} control={<Radio />} label="No" />
                                </RadioGroup>
                            </Grid>

                            {mentionAccounts && (
                                <Grid item xs={12}>
                                    {accountsList.map((account, index) => (
                                        <Box key={index} display="flex" gap={1} mb={2}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label={`Instagram Account ${index + 1}`}
                                                value={account}
                                                onChange={(e) => {
                                                    const newAccounts = [...accountsList];
                                                    newAccounts[index] = formatAccountName(e.target.value);
                                                    setAccountsList(newAccounts);
                                                    setFormValues(prev => ({
                                                        ...prev,
                                                        taggetAccount: newAccounts.filter(a => a).join(',')
                                                    }));
                                                }}
                                                placeholder="@instagram_account"
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">@</InputAdornment>,
                                                }}
                                            />
                                            {index !== 0 && (
                                                <IconButton
                                                    color="error"
                                                    onClick={() => {
                                                        const newAccounts = accountsList.filter((_, i) => i !== index);
                                                        setAccountsList(newAccounts);
                                                        setFormValues(prev => ({
                                                            ...prev,
                                                            taggetAccount: newAccounts.filter(a => a).join(',')
                                                        }));
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setAccountsList([...accountsList, ''])}
                                    >
                                        Add One More Account
                                    </Button>
                                </Grid>
                            )}


                        </Grid>

                    </Box>
                </Grid>

                {/* 2. Campaign & Concept Details */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2 }} >
                        <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1 }}>
                            Campaign & Concept Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size='small'
                                    fullWidth
                                    label="Campaign Name"
                                    name='campaignName'
                                    value={formValues?.campaignName}
                                    onChange={(e) => handleFormChange(e)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Official Instagram Audio Link"
                                    name="InstagramAudioLink"
                                    value={formValues?.InstagramAudioLink || ""}
                                    onChange={(e) => setFormValues(prev => ({ ...prev, InstagramAudioLink: e.target.value }))}
                                    placeholder="https://instagram.com/audio/example"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    multiline
                                    rows={3}
                                    size='small'
                                    fullWidth
                                    label="About Song (Special Notes)"
                                    name='specialNote'
                                    value={formValues?.specialNote}
                                    onChange={(e) => handleFormChange(e)}
                                    placeholder="Tell us about your song..."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Song Duration"
                                    required
                                    value={songDuration}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/[^0-9]/g, '');
                                        if (value.length > 2) {
                                            value = value.slice(0, 2) + ':' + value.slice(2, 4);
                                        }
                                        setSongDuration(value);
                                    }}
                                    placeholder="e.g., 03:45"
                                    inputProps={{ maxLength: 5 }}
                                />
                            </Grid>


                            <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label='Promotion Start Date *'
                                        value={isStartDateValid ? startDateValue : null}
                                        minDate={dayjs(minDate)}
                                        onChange={(date) => {
                                            setDateTouched((prev) => ({ ...prev, startDate: true }));
                                            releaseDateChangeHandler(date, 'startDate');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                {...params}
                                                error={Boolean(startDateErrorMessage) || Boolean(params.error)}
                                                helperText={startDateErrorMessage || params.helperText}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label='Promotion End Date *'
                                        value={isEndDateValid ? endDateValue : null}
                                        minDate={startDateValue && startDateValue.isValid() ? startDateValue : undefined}
                                        onChange={(date) => {
                                            setDateTouched((prev) => ({ ...prev, endDate: true }));
                                            releaseDateChangeHandler(date, 'endDate');
                                        }}
                                        disabled={!startDateValue || !startDateValue.isValid()}
                                        renderInput={(params) => (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                {...params}
                                                error={Boolean(endDateErrorMessage) || Boolean(params.error)}
                                                helperText={endDateErrorMessage || params.helperText}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                    </Box>
                    <Box sx={{ mb: 3, mt: 3, backgroundColor: '#fff', borderRadius: 2 }}>
                        <CardContent>
                            {/* Clips Section */}
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1 }}>
                                    Clips Timestamp (At least one clip required) *
                                </Typography>
                                {clips.map((clip, index) => (
                                    <Card key={index} sx={{ mb: 2, bgcolor: '#f9f9f9' }}>
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} >
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Start Time"
                                                        value={clip.start_time || ''}
                                                        required={index === 0}
                                                        placeholder="00:30"
                                                        error={Boolean(clipErrors[index]?.start_time)}
                                                        helperText={clipErrors[index]?.start_time}
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/[^0-9]/g, '');
                                                            if (value.length > 2) {
                                                                value = value.slice(0, 2) + ':' + value.slice(2, 4);
                                                            }
                                                            const newClips = [...clips];
                                                            newClips[index].start_time = value;
                                                            setClips(newClips);

                                                            const errors = [...clipErrors];
                                                            if (newClips[index].end_time && !isStartBeforeEnd(value, newClips[index].end_time)) {
                                                                errors[index] = { ...errors[index], end_time: "End time must be greater than start time" };
                                                            } else if (errors[index]?.end_time) {
                                                                delete errors[index].end_time;
                                                            }
                                                            if (errors[index]?.start_time) delete errors[index].start_time;
                                                            setClipErrors(errors);
                                                            validateClipTimes(newClips);
                                                        }}
                                                        inputProps={{ maxLength: 5 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="End Time"
                                                        required={index === 0}
                                                        value={clip.end_time || ''}
                                                        placeholder="01:00"
                                                        error={Boolean(clipErrors[index]?.end_time)}
                                                        helperText={clipErrors[index]?.end_time}
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/[^0-9]/g, '');
                                                            if (value.length > 2) {
                                                                value = value.slice(0, 2) + ':' + value.slice(2, 4);
                                                            }
                                                            const newClips = [...clips];
                                                            newClips[index].end_time = value;
                                                            setClips(newClips);

                                                            const errors = [...clipErrors];
                                                            if (newClips[index].start_time && !isStartBeforeEnd(newClips[index].start_time, value)) {
                                                                errors[index] = { ...errors[index], end_time: "End time must be greater than start time" };
                                                            } else if (errors[index]?.end_time) {
                                                                delete errors[index].end_time;
                                                            }
                                                            if (errors[index]?.start_time) delete errors[index].start_time;
                                                            setClipErrors(errors);
                                                            validateClipTimes(newClips);
                                                        }}
                                                        inputProps={{ maxLength: 5 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Reference Link"
                                                        value={clip.reference_link || ''}
                                                        placeholder="https://example.com"
                                                        error={Boolean(clipErrors[index]?.reference_link)}
                                                        helperText={clipErrors[index]?.reference_link}
                                                        onBlur={() => {
                                                            const errors = [...clipErrors];
                                                            if (clip.reference_link && !isValidUrl(clip.reference_link)) {
                                                                errors[index] = { ...errors[index], reference_link: "Enter a valid URL" };
                                                            } else {
                                                                if (errors[index]) delete errors[index].reference_link;
                                                            }
                                                            validateAllReferenceLinks(clips);
                                                            setClipErrors(errors);
                                                        }}
                                                        onChange={(e) => {
                                                            const newClips = [...clips];
                                                            newClips[index].reference_link = e.target.value;
                                                            setClips(newClips);
                                                            validateAllReferenceLinks(newClips);
                                                            if (clipErrors[index]?.reference_link) {
                                                                const errors = [...clipErrors];
                                                                delete errors[index]?.reference_link;
                                                                setClipErrors(errors);
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box display="flex" gap={1}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            label="Concept Description"
                                                            value={clip.description || ''}
                                                            placeholder="Concept..."
                                                            onChange={(e) => {
                                                                const newClips = [...clips];
                                                                newClips[index].description = e.target.value;
                                                                setClips(newClips);
                                                            }}
                                                        />
                                                        {index !== 0 && (
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => setClips(clips.filter((_, i) => i !== index))}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setClips([...clips, { start_time: '', end_time: '', description: '', reference_link: '' }])}
                                >
                                    Add Another Clip
                                </Button>
                            </Grid>
                        </CardContent>
                    </Box>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, mb: 1 }}>
                            Budget
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel>Currency</InputLabel>
                                    <Select
                                        name="currency"
                                        value={formValues?.currency || ""}
                                        onChange={(e) => {
                                            setFormValues(prev => ({
                                                ...prev,
                                                currency: e.target.value || ''
                                            }));
                                        }}
                                        input={<OutlinedInput label="Currency" />}
                                    >
                                        {currencyList?.map((cur) => (
                                            <MenuItem key={cur.id} value={cur.id}>
                                                {cur.code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Budget Amount"
                                    name="budget"
                                    type="number"
                                    // `?? ''` keeps the field CONTROLLED. The song/demo
                                    // effects rebuild `formValues` without a `budget` key, so
                                    // this was `undefined` after picking a song — and an
                                    // undefined value hands the input back to the DOM, where
                                    // React stops correcting what it displays.
                                    value={formValues?.budget ?? ''}
                                    onChange={(e) => {
                                        // Two separate reasons this holds the RAW STRING and
                                        // not `Number(...)`:
                                        //
                                        // 1. `Number('')` is 0, so clearing the box wrote a
                                        //    literal 0 into it — which is the "0" you see on a
                                        //    field nobody has filled in.
                                        // 2. React compares number inputs LOOSELY before it
                                        //    rewrites them (`node.value != props.value`). With
                                        //    "0" in the box, typing 5 makes the DOM "05", and
                                        //    "05" == 5 is true — so React saw no difference and
                                        //    left the leading zero sitting there. Stripping it
                                        //    here means the value React writes back really is
                                        //    different from what the DOM holds.
                                        const raw = e.target.value.replace(/^0+(?=\d)/, '');
                                        setFormValues(prev => ({ ...prev, budget: raw }));

                                        const amount = raw === '' ? 0 : Number(raw);
                                        if (!amount) {
                                            setBudgetError('Budget is required');
                                        } else if (amount < 125000) {
                                            setBudgetError('Budget must be at least ₹1,25,000');
                                        } else {
                                            setBudgetError('');
                                        }
                                    }}
                                    error={Boolean(budgetError)}
                                    helperText={budgetError || 'Minimum: ₹1,25,000'}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    }}
                                    placeholder="1,25,000"
                                    required
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid >
            </Grid >
        </Box >
    );
}
